import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Printer, ArrowLeft, ExternalLink } from "lucide-react";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const KNOWN_DOCS = [
  { slug: "modulos",          label: "Catálogo de Módulos" },
  { slug: "caso-exito",       label: "Caso de Éxito" },
  { slug: "propuesta-tania",  label: "Propuesta Comercial · Tania" },
  { slug: "logos",            label: "Propuestas de Logo" },
];

const MD_COMPONENTS = {
  img: ({ src, ...props }) => {
    const url = src?.startsWith("/api/") ? `${BACKEND}${src}` : src;
    return <img src={url} {...props} className="mx-auto rounded-lg" loading="lazy" alt={props.alt || ""} />;
  },
};

export default function DocPage() {
  const { slug } = useParams();
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setDoc(null); setError(null);
    axios.get(`${BACKEND}/api/docs/${slug}`)
      .then((r) => setDoc(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Error al cargar"));
  }, [slug]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center p-8">
        <div className="text-center">
          <h1 className="font-display text-3xl text-[#1A1C1A] mb-2">404</h1>
          <p className="text-[#5C5E5C] mb-6">{error}</p>
          <Link to="/" className="text-[#A69076] underline">Volver al inicio</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      {/* Toolbar (hidden on print) */}
      <div className="print:hidden sticky top-0 z-10 bg-white border-b border-[#E6E2DC]">
        <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 text-xs text-[#5C5E5C] hover:text-[#1A1C1A]">
            <ArrowLeft className="w-4 h-4" /> Inicio
          </Link>
          <div className="flex items-center gap-2">
            {KNOWN_DOCS.filter((d) => d.slug !== slug).map((d) => (
              <Link key={d.slug} to={`/docs/${d.slug}`} className="text-[10px] tracking-[0.18em] uppercase text-[#A69076] hover:text-[#1A1C1A] flex items-center gap-1">
                {d.label} <ExternalLink className="w-3 h-3" />
              </Link>
            ))}
            <button
              onClick={() => window.print()}
              className="ml-3 inline-flex items-center gap-2 rounded-full bg-[#1A1C1A] hover:bg-[#A69076] text-white text-[10px] uppercase tracking-[0.2em] px-4 h-9"
              data-testid="doc-print"
            >
              <Printer className="w-3.5 h-3.5" /> Imprimir / PDF
            </button>
          </div>
        </div>
      </div>

      {/* Document */}
      <article className="max-w-4xl mx-auto px-6 sm:px-10 py-10 print:py-4 print:max-w-full bg-white shadow-sm my-6 print:shadow-none print:my-0 rounded-2xl print:rounded-none">
        {doc ? (
          <div className="prose prose-stone max-w-none font-body
                            prose-headings:font-display prose-headings:text-[#1A1C1A]
                            prose-h1:text-4xl prose-h1:tracking-tight
                            prose-h2:text-2xl prose-h2:mt-10 prose-h2:border-b prose-h2:border-[#EFEAE2] prose-h2:pb-2
                            prose-h3:text-xl prose-h3:text-[#3C3E3C]
                            prose-p:text-[#3C3E3C] prose-p:leading-relaxed
                            prose-a:text-[#A69076] prose-a:no-underline hover:prose-a:underline
                            prose-strong:text-[#1A1C1A]
                            prose-table:text-sm
                            prose-th:bg-[#F4F0EB] prose-th:text-[#1A1C1A]
                            prose-blockquote:border-l-[#A69076] prose-blockquote:text-[#5C5E5C]
                            prose-code:text-[#D97757] prose-code:bg-[#F4F0EB] prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-[12px] prose-code:before:content-[''] prose-code:after:content-['']
                            prose-li:text-[#3C3E3C]">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>{doc.markdown}</ReactMarkdown>
          </div>
        ) : (
          <div className="py-20 text-center text-[#A69076] uppercase tracking-[0.2em] text-xs">Cargando…</div>
        )}
      </article>
    </div>
  );
}
